import { useRouter } from 'next/router';
import { useMemo } from 'react';

import { GetServerSideProps } from 'next';

import { LayoutContent } from '@/components/layout/LayoutComponents';
import { Role } from '@/models/authModels';
import { CustomTab } from '@/models/customizationModels';
import { selectCustomTabs } from '@/redux/customizations/reducers';
import { useAppSelector } from '@/redux/hooks';
import { getCommonServerSideProps } from '@/util/getServerSideProps';
import AccessConsume from '@/wrappers/auth/AccessConsume';
import LayoutConsume from '@/wrappers/layout/LayoutConsume';

export default function CustomTabPage() {
  const router = useRouter();
  const customTabs = useAppSelector(selectCustomTabs);
  const selectedCampaign = useAppSelector((state) => state.campaigns.selectedCampaign);
  const ambassador = useAppSelector((state) => state.auth.ambassadorProfile);

  const { slug } = router.query;

  const customTab = useMemo(() => {
    return customTabs?.find((customTab: CustomTab) => slug && customTab.slug === slug[0]);
  }, [slug, customTabs]);

  const shortCode = useMemo(() => {
    if (selectedCampaign?.short_code) return selectedCampaign.short_code;
    const urls = ambassador?.urls ?? [];
    const selectedCampaignUrl = urls.find((url) => url.campaign_id === selectedCampaign?.campaign_id && url.short_code);
    return selectedCampaignUrl?.short_code ?? urls.find((url) => url.short_code)?.short_code ?? '';
  }, [selectedCampaign, ambassador]);

  const source = useMemo(() => {
    if (!customTab) return '';

    const replacements: Record<string, string> = {
      sf_contact_id: ambassador?.sf_contact_id ?? '',
      sf_ambassador_id: ambassador?.sf_ambassador_id ?? '',
      email: ambassador?.email ?? '',
      custom1: ambassador?.custom1 ?? '',
      custom2: ambassador?.custom2 ?? '',
      custom3: ambassador?.custom3 ?? '',
      custom4: ambassador?.custom4 ?? '',
      custom5: ambassador?.custom5 ?? '',
      custom6: ambassador?.custom6 ?? '',
      custom7: ambassador?.custom7 ?? '',
      custom8: ambassador?.custom8 ?? '',
      custom9: ambassador?.custom9 ?? '',
      custom10: ambassador?.custom10 ?? '',
      affiliate_id: ambassador?.uid.toString() ?? '',
      short_code: shortCode,
    };

    return customTab.source.replace(/\{([^}]+)\}/g, (match, key) => (key in replacements ? replacements[key] : match));
  }, [customTab, shortCode, ambassador]);

  if (!customTab) return null;

  return (
    <AccessConsume roles={[Role.admin]}>
      <LayoutConsume itemActive={customTab.slug} menuActive={true}>
        <LayoutContent>
          <div className="container-fluid h-100 d-flex justify-content-center align-items-center p-0">
            <div className="row w-100 h-100 m-0">
              <div className="col-12 p-0">
                <iframe className="w-100 border-0 min-vh-100" src={source} />
              </div>
            </div>
          </div>
        </LayoutContent>
      </LayoutConsume>
    </AccessConsume>
  );
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const commonProps = await getCommonServerSideProps(context);
  return {
    props: {
      ...commonProps,
    },
  };
};
